import { useEffect } from "react";

interface HotkeyOptions {
  enabled?: boolean;
  preventDefault?: boolean;
}

/**
 * Bind a global keyboard shortcut. Combos use `+`-separated tokens (e.g.
 * `meta+k`, `shift+/`, `g h`). Sequences are joined with a single space.
 */
export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  { enabled = true, preventDefault = true }: HotkeyOptions = {},
): void {
  useEffect(() => {
    if (!enabled) return;
    function matches(e: KeyboardEvent, c: string): boolean {
      const parts = c
        .toLowerCase()
        .split("+")
        .map((p) => p.trim());
      const expectMeta = parts.includes("meta") || parts.includes("cmd");
      const expectCtrl = parts.includes("ctrl");
      const expectShift = parts.includes("shift");
      const expectAlt = parts.includes("alt");
      const key = parts[parts.length - 1] ?? "";
      if (expectMeta !== (e.metaKey || e.ctrlKey)) return false;
      if (expectCtrl && !e.ctrlKey) return false;
      if (expectShift !== e.shiftKey) return false;
      if (expectAlt !== e.altKey) return false;
      return e.key.toLowerCase() === key;
    }
    function onKey(e: KeyboardEvent): void {
      if (matches(e, combo)) {
        if (preventDefault) e.preventDefault();
        handler(e);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [combo, handler, enabled, preventDefault]);
}
